import { Locker } from "shared/locker"
import { LockerThingTypeMrn } from "shared/lockerData"
import { appendNoteLine } from "./appendNoteLine"

/**
 * The operator SERVICE SWITCH — take one locker out of service by hand.
 *
 * ## Why this exists next to the migration override, instead of inside it
 *
 * A V1→V2 migration decides user-visibility with three attributes
 * (`migrationDisabled`, `pendingActivation`, `supersededByLockerThingId`), and
 * `SaLockerMigrationOverride` lets an operator write them directly to repair a
 * half-landed cutover. That panel is the right tool for repair and the wrong tool
 * for a panic button, for one reason: those three are EFFECTS of the migration
 * state machine and are re-asserted unconditionally on every partner event
 * redelivery — `migrationLockerOperationalHandler` deliberately has no
 * "already cut over, skip" path, and the partner has been observed emitting
 * `operational` twice ~95µs apart. An operator edit there can therefore be
 * reverted seconds later, silently.
 *
 * `outOfService` is written by a human and by NOTHING else, so it sticks. That
 * durability is the entire feature.
 *
 * ## Deliberately subtractive: it can hide, never reveal
 *
 * There is no "force visible" here, and that asymmetry is not an oversight:
 *
 *   hiding  is safe — nothing downstream has to work for a hidden locker to be
 *           correct. It drops out of `Ap_GetLockers` and refuses new
 *           reservations. Done.
 *   showing is not — `assertLockerNotSuperseded` refuses EVERY door action while
 *           `supersededByLockerThingId` is set. A locker revealed by a cosmetic
 *           flag would be listed, bookable, and unable to open a single door,
 *           which is worse than being hidden. Restoring service means clearing
 *           the migration pointer, which is the override panel's job.
 *
 * ## Scope of the hide
 *
 * NEW business only: user-facing listings (`Ap_GetLockers`) and new reservations
 * (`newReservationRequestHandler`). Door actions on reservations that already
 * exist keep working, because a parcel behind a door has to stay retrievable.
 * "Out of service" means no new business, not bricked.
 *
 * Pure on purpose — the React panel is a thin shell over this module.
 */

export type LockerServiceState = "inService" | "outOfService"

/** `outOfServiceNote` is capped at 10 000 chars by `lockerAttrsUpdateSchema`. */
export const SERVICE_NOTE_MAX = 10000

/** Absent attribute == in service, matching the fail-open read gate. */
export const isOutOfService = (locker?: Partial<Locker> | null): boolean =>
  !!locker?.outOfService

export const readServiceState = (
  locker?: Partial<Locker> | null,
): LockerServiceState => (isOutOfService(locker) ? "outOfService" : "inService")

/** A single Thing patch, shaped for `updateThings({ body: { things } })`. */
export type LockerServicePatch = {
  id: string
  tenantId: string
  thingTypeMrn: string
  outOfService: boolean
  outOfServiceNote: string
}

/** Newest entry first, so the head of the note is always the current reason. */
export const appendServiceNote = (
  existing: string | undefined,
  line: string,
): string => appendNoteLine(existing, line, SERVICE_NOTE_MAX)

const SERVICE_LABELS: Record<LockerServiceState, string> = {
  inService: "IN_SERVICE",
  outOfService: "OUT_OF_SERVICE",
}

const serviceNoteLine = (args: {
  state: LockerServiceState
  reason: string
  now: string
  actor?: string
}): string => {
  const by = args.actor ? ` by ${args.actor}` : ""
  return `[service switch ${args.now}${by}] ${SERVICE_LABELS[args.state]} — ${args.reason}`
}

export type BuildServicePatchArgs = {
  locker: Locker
  targetState: LockerServiceState
  reason: string
  actor?: string
  /** Injected so the output is deterministic in tests. */
  now: string
}

/**
 * Exactly one Thing is ever written. Unlike the migration override there is no
 * paired write: the switch is a statement about ONE physical unit's availability,
 * and touching the counterpart would silently take a second unit out of service.
 */
export const buildServicePatch = (
  args: BuildServicePatchArgs,
): LockerServicePatch => {
  const { locker, targetState, reason, actor, now } = args
  return {
    id: locker.id,
    tenantId: locker.tenantId,
    thingTypeMrn: LockerThingTypeMrn,
    outOfService: targetState === "outOfService",
    outOfServiceNote: appendServiceNote(
      locker.outOfServiceNote,
      serviceNoteLine({ state: targetState, reason, now, actor }),
    ),
  }
}

/**
 * Field-level diff for the confirmation dialog. Raw attribute name on purpose —
 * paraphrasing the write is how an operator approves something other than what
 * lands in DynamoDB.
 */
export const summarizeServicePatch = (
  patch: LockerServicePatch,
  before: boolean,
): string[] =>
  patch.outOfService === before
    ? []
    : [`outOfService: ${String(before)} → ${String(patch.outOfService)}`]
